import React, { useState } from 'react';
import { Form, Input, Button, Row, Col, Switch } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useDialog } from './DialogProvider';

function TrainingForm({
    steps,
    setSteps,
    duplicateSelectedSteps,
    deleteSelectedSteps,
    setIsModificationMade,
    createWarmUpSteps,
    FTP,
    setFTP,
    refFTPForAdjust,
    setRefFTPForAdjust,
    onAdjustPower,
    lastNon100FTP = null,
    setLastNon100FTP = () => { },
    powerUnit = "watts", // "watts" | "percent" (display/input mode only; steps are always stored in watts)
    setPowerUnit = () => { },
}) {
    const { alert: alertDlg } = useDialog();
    const [duration, setDuration] = useState(null);
    const [watt, setWatt] = useState(null);
    const [fromWatt, setFromWatt] = useState(null);
    const [toWatt, setToWatt] = useState(null);
    //const [stepWatt, setStepWatt] = useState(null);
    const [comment, setComment] = useState('');
    const [warmUp, setWarmUp] = useState(false);
    const [addingMode, setAddingMode] = useState("add");

    const isPercentMode = powerUnit === "percent";

    const handleSubmit = (event) => {

        const minStepDuration = 10;
        const maxStepDuration = 3600 * 10;
        const minWatt = 10;
        const maxWatt = 1500;
        const minPct = 10;
        const maxPct = 300;
        const maxSteps = 100;

        const asInt = (v) => {
            if (v === null || v === undefined || v === "") return null;
            const n = parseInt(v, 10);
            return Number.isFinite(n) ? n : null;
        };

        const validateStep = (s) => {
            const d = asInt(s?.duration);
            const w = asInt(s?.watt);
            if (d === null || d < minStepDuration || d > maxStepDuration) return false;
            if (w === null || w < minWatt || w > maxWatt) return false;
            return true;
        };

        // In %FTP mode, inputs are interpreted as a percentage of current FTP,
        // but we always store step targets as absolute watts.
        const ftpNum = Number(FTP);
        const ftpSafe = Number.isFinite(ftpNum) && ftpNum > 0 ? ftpNum : null;
        if (isPercentMode && !ftpSafe) {
            alertDlg("Please set a valid FTP before using %FTP mode.");
            return;
        }

        const toWatts = (valInt) => {
            if (valInt === null) return null;
            if (!isPercentMode) return valInt;
            // valInt is percent; convert to watts
            return Math.round((valInt * ftpSafe) / 100);
        };

        const validateInputPower = (valInt) => {
            if (valInt === null) return false;
            if (!isPercentMode) return valInt >= minWatt && valInt <= maxWatt;
            return valInt >= minPct && valInt <= maxPct;
        };

        let newSteps;
        const durationInt = asInt(duration);
        if (!warmUp) {
            newSteps = [];

            const wattInt = asInt(watt);
            if (durationInt === null || wattInt === null) {
                alertDlg(`Duration and power are required!`);
                return;
            }
            if (durationInt < minStepDuration || durationInt > maxStepDuration) {
                alertDlg(`Duration must be between ${minStepDuration} and ${maxStepDuration} seconds.`);
                return;
            }
            if (!validateInputPower(wattInt)) {
                alertDlg(isPercentMode ? `%FTP must be between ${minPct} and ${maxPct}.` : `Watts must be between ${minWatt} and ${maxWatt}.`);
                return;
            }
            const watts = toWatts(wattInt);
            newSteps.push({ duration: durationInt, watt: watts, comment: comment, selected: false });
        }
        else {
            const fromWattInt = asInt(fromWatt);
            const toWattInt = asInt(toWatt);
            if (durationInt === null) {
                alertDlg(`Duration is required!`);
                return;
            }
            if (durationInt < minStepDuration || durationInt > maxStepDuration) {
                alertDlg(`Duration must be between ${minStepDuration} and ${maxStepDuration} seconds.`);
                return;
            }
            if (fromWattInt === null || toWattInt === null) {
                alertDlg(`From/To power are required!`);
                return;
            }
            if (!validateInputPower(fromWattInt) || !validateInputPower(toWattInt)) {
                alertDlg(
                    isPercentMode
                        ? `%FTP must be between ${minPct} and ${maxPct}.`
                        : `Watts must be between ${minWatt} and ${maxWatt}.`
                );
                return;
            }

            const fromWatts = toWatts(fromWattInt);
            const toWattsVal = toWatts(toWattInt);
            newSteps = createWarmUpSteps(durationInt, fromWatts, toWattsVal, comment);
            if (!newSteps) return; // createWarmUpSteps returns undefined on validation failure

        }

        // Safety: ensure *all* generated steps match constraints (including warmup generators).
        if (!Array.isArray(newSteps) || newSteps.length === 0) {
            alertDlg(`No valid steps generated.`);
            return;
        }
        const invalidStep = newSteps.find((s) => !validateStep(s));
        if (invalidStep) {
            alertDlg(`Each step must have duration ${minStepDuration}-${maxStepDuration}s and watts ${minWatt}-${maxWatt}.`);
            return;
        }

        // Enforce max steps cap depending on the operation mode.
        let candidateSteps;
        if (addingMode === "add") {
            candidateSteps = [...steps, ...newSteps];
        }
        else if (addingMode === "modify") {
            // Replace each selected step with the generated steps
            candidateSteps = steps.map((x) => x.selected ? newSteps : x).flat();
        }
        else if (addingMode === "addBefore") {
            // Insert generated steps before each selected step
            candidateSteps = steps.map((x) => x.selected ? [...newSteps, x] : x).flat();
        }
        else {
            candidateSteps = [...steps];
        }

        if (candidateSteps.length > maxSteps) {
            alertDlg(`Max steps is ${maxSteps}. This operation would create ${candidateSteps.length} steps.`);
            return;
        }

        setSteps(candidateSteps);
        setIsModificationMade(true)

    };

    return (
        <div className="glass-panel liquid-glass">
        <Form onFinish={handleSubmit} className="liquid-glass__form">
            <Row gutter={16}>
                <Col span={2}>
                    <Button
                        block
                        className={isPercentMode ? "primary-glass-btn" : ""}
                        type={isPercentMode ? "primary" : "default"}
                        onClick={() => {
                            try {
                                setPowerUnit(isPercentMode ? "watts" : "percent");
                            } catch { }
                        }}
                    >
                        % FTP {isPercentMode && <CheckOutlined />}
                    </Button>
                </Col>
                <Col span={2}>
                    <Switch
                        block
                        checked={warmUp}
                        onChange={(c) => setWarmUp(c)}
                        checkedChildren="Ramp up"
                        unCheckedChildren="Ramp up"
                    />
                </Col>
                <Col span={6}>
                    <Form.Item
                        label="Duration"
                        name="duration"
                        rules={[{ required: true, message: 'Please input the duration!' }]}
                    >
                        <Input
                            type="number"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            placeholder="Duration"
                            addonAfter="Secs"
                            min={10}
                            max={3600 * 10}

                        />
                    </Form.Item>
                </Col>

                {!warmUp ?
                    <Col span={6}>
                        <Form.Item
                            label="Power"
                            name="watt"
                            rules={[{ required: false, message: 'Please input the wattage!' }]}
                        >
                            <Input
                                type="number"
                                value={watt}
                                onChange={(e) => setWatt(e.target.value)}
                                placeholder={"Power"}
                                addonAfter={isPercentMode ? "% FTP" : "Watts"}
                                    min={isPercentMode ? 10 : 10}
                                    max={isPercentMode ? 300 : 1500}

                            />
                        </Form.Item>
                    </Col> : <>
                        <Col span={4}>
                            <Form.Item
                                label="From"
                                name="fromwatt"
                            //rules={[{ required: true, message: 'Please input the wattage!' }]}
                            >
                                <Input
                                    type="number"
                                    value={fromWatt}
                                    onChange={(e) => setFromWatt(e.target.value)}
                                    placeholder={"From"}
                                    addonAfter={isPercentMode ? "% FTP" : "Watts"}
                                    min={isPercentMode ? 10 : 10}
                                    max={isPercentMode ? 300 : 1500}

                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                label="To"
                                name="towatt"
                            //rules={[{ required: true, message: 'Please input the wattage!' }]}
                            >
                                <Input
                                    type="number"
                                    value={toWatt}
                                    onChange={(e) => setToWatt(e.target.value)}
                                    placeholder="To"
                                    addonAfter={isPercentMode ? "% FTP" : "Watts"}
                                    min={isPercentMode ? 10 : 10}
                                    max={isPercentMode ? 300 : 1500}

                                />
                            </Form.Item>
                        </Col>

                        {/* <Col span={3} hidden={true}>
                            <Form.Item
                                label="Step Watts"
                                name="stepwatt"
                            //rules={[{ required: true, message: 'Please input the wattage!' }]}
                            >
                                <Input
                                    type="number"
                                    value={stepWatt}
                                    onChange={(e) => setStepWatt(e.target.value)}
                                    placeholder="Average Watts"
                                />
                            </Form.Item>
                        </Col> */}
                    </>
                }
                <Col span={warmUp ? 6 : 8}>
                    <Form.Item
                        label="Comment"
                        name="comment"
                    >
                        <Input
                            type="text"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Write ** RPM in the text to set a target RPM"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={4}>
                    <Form.Item>
                        <Button
                            block
                            type="primary"
                            htmlType="submit"
                            onClick={() => setAddingMode("add")}
                            disabled={(!duration || (!watt && (!toWatt || !fromWatt)) || duration.trim() === "")}
                        >
                            Add
                        </Button>
                    </Form.Item>
                </Col>
                <Col span={1}></Col>
                <Col span={4}>
                    <Form.Item>
                        <Button
                            block
                            //type="primary"
                            htmlType="submit"
                            onClick={() => setAddingMode("addBefore")}
                            disabled={(!duration || (!watt && (!toWatt || !fromWatt)) || duration.trim() === "") || steps.filter((x) => x.selected).length === 0}
                        >
                            Add Before Selected
                        </Button>
                    </Form.Item>
                </Col>
                <Col span={1}></Col>
                <Col span={4}>
                    <Form.Item>
                        <Button
                            block
                            //type="primary"

                            htmlType="submit"
                            onClick={() => setAddingMode("modify")}
                            disabled={(!duration || (!watt && (!toWatt || !fromWatt)) || duration.trim() === "") || steps.filter((x) => x.selected).length === 0}
                        >
                            Replace Selected
                        </Button>
                    </Form.Item>
                </Col>
                <Col span={1}></Col>
                <Col span={4}>
                    <Form.Item>
                        <Button disabled={steps.filter((x) => x.selected).length === 0} onClick={duplicateSelectedSteps} block>
                            Duplicate Selected
                        </Button>
                    </Form.Item>
                </Col>
                <Col span={1}></Col>
                <Col span={4}><Form.Item><Button block danger disabled={steps.filter((x) => x.selected).length === 0} onClick={deleteSelectedSteps}>Delete Selected</Button></Form.Item></Col>
            </Row>
        </Form >
        </div>
    );


}

export default TrainingForm;
